import { ChangeDetectorRef, Component, effect, input, OnDestroy, OnInit, output, inject } from '@angular/core';

import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CropField } from '@next/arable-lib/crop-field';
import { debounce, distinctUntilChanged, interval, ReplaySubject, Subject, takeUntil } from 'rxjs';

import { QuestionnaireAnswer } from 'src/app/tga/shared/models/models';

import { TgaInputType, TGAQuestion, TGATableQuestion } from '../../models/question-models';
import { updateVisibilityOnDependantQuestions } from '../question-helpers';
import { FormComponent } from '@next/core-lib/form';
import { QuestionFormComponent } from '../question-form/question-form.component';

@Component({
  selector: 'app-questions-list-form',
  templateUrl: './questions-list-form.component.html',
  styleUrls: ['./questions-list-form.component.scss'],
  imports: [FormComponent, FormsModule, ReactiveFormsModule, QuestionFormComponent],
})
export class QuestionsListFormComponent implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  questions = input.required<TGAQuestion<TgaInputType>[]>();
  formGroup = input.required<FormGroup>();
  cropField = input<CropField>();
  readOnly = input(false);
  formStateUpdater = input<((questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup) => void) | undefined>(
    undefined,
  );
  answersUpdated = output<FormGroup>();
  publishChangeDelayInMs = 800;
  changePending = false;

  private readonly valueChange$ = new Subject<QuestionnaireAnswer>();
  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private orderedKeysForScroll?: string[];

  constructor() {
    effect(() => {
      if (this.readOnly()) {
        this.changePending = false;
      }
    });
  }

  ngOnInit(): void {
    this.valueChange$
      .pipe(
        distinctUntilChanged(),
        debounce(() => {
          this.changePending = true;
          return interval(this.publishChangeDelayInMs);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.publishFormUpdate();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    if (this.changePending) {
      this.publishFormUpdate();
    }
  }

  handleValueChange(event: QuestionnaireAnswer) {
    if (this.readOnly()) return;

    this.valueChange$.next(event);
    this.formStateUpdater()?.(this.questions(), this.formGroup());

    const questions = this.questions();
    const orderedKeys: string[] = [];
    for (const q of questions) {
      if (q.controlType === 'table' && (q as TGATableQuestion).subQuestions) {
        for (const sub of (q as TGATableQuestion).subQuestions) {
          orderedKeys.push(sub.key);
        }
      } else {
        orderedKeys.push(q.key);
      }
    }

    const lastInteractiveKey = orderedKeys.at(-1);
    const eventKeyStr = String(event.key);
    const lastInteractiveKeyStr = lastInteractiveKey ? String(lastInteractiveKey) : undefined;

    if (!lastInteractiveKey || eventKeyStr !== lastInteractiveKeyStr) {
      return;
    }

    const formGroup = this.formGroup();
    (formGroup as FormGroup & { __showValidation?: boolean }).__showValidation = true;

    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
      }
    }

    this.cdr.detectChanges();

    setTimeout(() => {
      this.orderedKeysForScroll = orderedKeys;
      this.scrollToFirstInvalidField();
      this.orderedKeysForScroll = undefined;
    }, 100);
  }

  private publishFormUpdate() {
    if (this.readOnly() || !this.changePending) {
      this.changePending = false;
      return;
    }

    const formGroupValue = this.formGroup();
    formGroupValue.markAsDirty();

    if (this.changePending) {
      updateVisibilityOnDependantQuestions(this.questions(), this.formGroup());
      this.formStateUpdater()?.(this.questions(), this.formGroup());
      this.changePending = false;
      this.answersUpdated.emit(formGroupValue);
      this.cdr.detectChanges();
    }
  }

  private scrollToFirstInvalidField(): void {
    const questions = this.questions();
    const formGroup = this.formGroup();
    const orderedKeys: string[] | undefined = this.orderedKeysForScroll;
    if (orderedKeys && orderedKeys.length > 0) {
      this.scrollToFirstInvalidUsingKeys(orderedKeys, formGroup);
      return;
    }

    this.scrollToFirstInvalidUsingQuestions(questions, formGroup);
  }

  private scrollToFirstInvalidUsingKeys(keys: string[], formGroup: FormGroup): void {
    for (const key of keys) {
      const control = formGroup.get(key);
      if (control && control.invalid && (control.dirty || control.touched)) {
        const elementId = `question-${key}`;
        const element = document.getElementById(elementId);

        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          break;
        }
      }
    }
  }

  private scrollToFirstInvalidUsingQuestions(questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup): void {
    for (const question of questions) {
      const control = formGroup.get(question.key);
      if (control && control.invalid && question.required && (control.dirty || control.touched)) {
        const elementId = `question-${question.key}`;
        const element = document.getElementById(elementId);

        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          break;
        }
      }
    }
  }
}
